import {useMemo} from 'react';
import {useDevice} from '@lookiero/aurora';
import {Country, Locale} from '@domain/model/Site/Locale';
import {Platform} from '@domain/model/App/Platform';
import {useLocale} from './useLocale';
import {TradeName} from '@domain/model/Config/Configuration';
import {ConfigurationApplication} from '@application/Config';

const tradeName = ConfigurationApplication.retrieve().tradename;

const APP_IDS: Record<TradeName, {android: string; ios: string}> = {
  [TradeName.LOOKIERO]: {android: 'com.lookiero.uaf.app', ios: '1551933506'},
  [TradeName.OUTFITTERY]: {android: 'de.outfittery.app', ios: '842551056'},
};

const getAndroidStore = (): string => {
  return `https://play.google.com/store/apps/details?id=${APP_IDS[tradeName].android}`;
};

const getIosStore = (country: Country): string => {
  return `https://apps.apple.com/${country}/app/id${APP_IDS[tradeName].ios}`;
};

const getNativeStore = (country: Country, platform: Platform): string | null => {
  if (platform.ios) {
    return getIosStore(country);
  }
  if (platform.android) {
    return getAndroidStore();
  }
  return null;
};

interface StoreResult {
  androidStore: string;
  iosStore: string;
  nativeStore: string | null;
}

export const useStore = (): StoreResult => {
  const {country} = useLocale() as Locale;
  const {platform} = useDevice();

  return useMemo(
    () => ({
      androidStore: getAndroidStore(),
      iosStore: getIosStore(country),
      nativeStore: getNativeStore(country, platform),
    }),
    [country, platform],
  );
};
